<template>
  <div class="course-gpt">
    <Sidebar />
    
    <div class="gpt-container">
      <div class="header">
        <h1>GPT Course Management</h1>
        <p class="subtitle">Manage your courses through natural language commands</p>
      </div>
      
      <div class="courses-display" v-if="courses.length > 0">
        <h2>Current Courses</h2>
        <div class="courses-grid">
          <div v-for="course in courses" :key="course.id" class="course-card">
            <div class="status-badge" :class="{ 'active': course.status === 'published', 'inactive': course.status === 'draft' }">
              {{ course.status }}
            </div>
            <img v-if="course.thumbnail_url" :src="course.thumbnail_url" class="course-image" :alt="course.title">
            <div v-else class="course-image-placeholder">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/>
              </svg>
            </div>
            <div class="course-content">
              <h3>{{ course.title }}</h3>
              <p class="course-lessons">Lessons: {{ course.lessons_count || 0 }}</p>
              <p class="course-description">{{ truncateDescription(course.description) }}</p>
            </div>
          </div>
        </div>
      </div>
      
      <div class="chat-interface">
        <div class="chat-messages">
          <div v-for="(message, index) in chatHistory" :key="index" 
               class="message" :class="{ 'user-message': message.type === 'user', 'assistant-message': message.type === 'assistant', 'system-message': message.type === 'system' }">
            <div class="message-icon">
              <i v-if="message.type === 'user'" class="fas fa-user"></i>
              <i v-else-if="message.type === 'assistant'" class="fas fa-robot"></i>
              <i v-else class="fas fa-info-circle"></i>
            </div>
            <div class="message-content" v-html="message.content"></div>
          </div>
        </div>
        
        <div class="user-input">
          <textarea 
            v-model="userPrompt" 
            placeholder="Type your course management command (e.g., 'Create a new course about Vue.js')"
            @keydown.enter.prevent="submitPrompt"
            :disabled="isProcessing"
            rows="2"
          ></textarea>
          <button @click="submitPrompt" :disabled="isProcessing || !userPrompt.trim()">
            <i class="fas" :class="isProcessing ? 'fa-spinner fa-spin' : 'fa-paper-plane'"></i>
          </button>
        </div>
        
        <div class="command-examples">
          <h3>Example Commands:</h3>
          <ul>
            <li @click="fillPrompt('Create a new course called Advanced JavaScript with description: Learn advanced concepts in JavaScript programming')">
              Create a new course called "Advanced JavaScript" with description: Learn advanced concepts in JavaScript programming
            </li>
            <li @click="fillPrompt('Update course with ID 3 to change the title to Modern React Development')">
              Update course with ID 3 to change the title to "Modern React Development"
            </li>
            <li @click="fillPrompt('Delete the course with ID 5')">
              Delete the course with ID 5
            </li>
            <li @click="fillPrompt('Change status of course with ID 2 to published')">
              Change status of course with ID 2 to published
            </li>
            <li @click="fillPrompt('Show me all published courses')">
              Show me all published courses
            </li>
          </ul>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Sidebar from '../Sidebar.vue';
import axios from 'axios';

export default {
  name: 'CourseGPT',
  components: { Sidebar },
  data() {
    return {
      userPrompt: '',
      chatHistory: [
        {
          type: 'system',
          content: 'Welcome to the GPT Course Manager! I can help you create, update, delete, and manage courses through natural language commands. Type your requests below.'
        }
      ],
      courses: [],
      loading: false,
      error: null,
      isProcessing: false
    };
  },
  async created() {
    await this.fetchCourses();
    this.addSystemMessage('I\'ve loaded your current courses. You have ' + this.courses.length + ' courses.');
  },
  methods: {
    async fetchCourses() {
      try {
        this.loading = true;
        const token = localStorage.getItem('token');
        const response = await axios.get('https://api.kewaar.com/api/admin/courses', {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Accept': 'application/json'
          }
        });
        
        this.courses = response.data.data.courses || [];
        
      } catch (error) {
        console.error('Error fetching courses:', error);
        this.error = error.response?.data?.message || error.message;
        this.addSystemMessage('Error: ' + (error.response?.data?.message || error.message));
      } finally {
        this.loading = false;
      }
    },
    
    async submitPrompt() {
      if (!this.userPrompt.trim() || this.isProcessing) return;
      
      // Add user message to chat history
      this.addUserMessage(this.userPrompt);
      const prompt = this.userPrompt;
      this.userPrompt = '';
      this.isProcessing = true;
      
      try {
        // Process natural language command
        const result = await this.processCourseCommand(prompt);
        
        // Add response to chat history
        this.addAssistantMessage(result.message || 'Command processed successfully');
        
        // Refresh courses if needed
        if (['create', 'update', 'delete', 'status'].includes(result.action)) {
          await this.fetchCourses();
        }
      } catch (error) {
        this.addSystemMessage('Error: ' + (error.message || 'An unknown error occurred.'));
      } finally {
        this.isProcessing = false;
      }
    },
    
    async processCourseCommand(prompt) {
      prompt = prompt.toLowerCase().trim();
      
      try {
        // Handle different command types
        if (this.isCreateCommand(prompt)) {
          return this.handleCreateCommand(prompt);
        } else if (this.isUpdateCommand(prompt)) {
          return this.handleUpdateCommand(prompt);
        } else if (this.isDeleteCommand(prompt)) {
          return this.handleDeleteCommand(prompt);
        } else if (this.isStatusCommand(prompt)) {
          return this.handleStatusCommand(prompt);
        } else if (this.isListCommand(prompt)) {
          return this.handleListCommand(prompt);
        } else {
          return {
            action: null,
            message: "I'm sorry, I couldn't understand that command. Please try one of the example commands or rephrase your request."
          };
        }
      } catch (error) {
        console.error('Error processing command:', error);
        return {
          action: null,
          message: `Error processing your request: ${error.message}`
        };
      }
    },
    
    // Command identification methods
    isCreateCommand(prompt) {
      return prompt.includes('create') || prompt.includes('add') || prompt.includes('new');
    },
    
    isUpdateCommand(prompt) {
      return prompt.includes('update') || prompt.includes('edit') || prompt.includes('modify') || prompt.includes('change');
    },
    
    isDeleteCommand(prompt) {
      return prompt.includes('delete') || prompt.includes('remove');
    },
    
    isStatusCommand(prompt) {
      return prompt.includes('status') || prompt.includes('publish') || prompt.includes('draft');
    },
    
    isListCommand(prompt) {
      return prompt.includes('list') || prompt.includes('show') || prompt.includes('display') || 
             prompt.includes('get') || prompt.includes('all') || prompt.includes('find');
    },
    
    // Command handlers
    async handleCreateCommand(prompt) {
      // Extract course title
      let title = '';
      const titleMatches = prompt.match(/called\s+"([^"]+)"/) || prompt.match(/called\s+([^\s,]+)/) ||
                           prompt.match(/title\s+"([^"]+)"/) || prompt.match(/title\s+([^\s,]+)/) || 
                           prompt.match(/course\s+"([^"]+)"/) || prompt.match(/course\s+([^\s,]+)/);
      
      if (titleMatches && titleMatches[1]) {
        title = titleMatches[1];
      }
      
      // Extract description
      let description = '';
      const descMatches = prompt.match(/description:?\s+"([^"]+)"/) || 
                          prompt.match(/description:?\s+(.+)$/);
      
      if (descMatches && descMatches[1]) {
        description = descMatches[1];
      }
      
      if (!title) {
        return {
          action: null,
          message: "I couldn't extract a course title from your command. Please provide a title for the course."
        };
      }
      
      try {
        const token = localStorage.getItem('token');
        const formData = new FormData();
        formData.append('title', title);
        
        if (description) {
          formData.append('description', description);
        }
        
        const response = await axios.post('https://api.kewaar.com/api/admin/courses', 
          formData,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Accept': 'application/json',
              'Content-Type': 'multipart/form-data'
            }
          }
        );
        
        return {
          action: 'create',
          message: `Successfully created new course "${title}" with ID ${response.data.data.id}`
        };
      } catch (error) {
        return {
          action: null,
          message: `Failed to create course: ${error.response?.data?.message || error.message}`
        };
      }
    },
    
    async handleUpdateCommand(prompt) {
      // First try to find by ID
      const idMatches = prompt.match(/id\s+(\d+)/) || prompt.match(/course\s+(\d+)/);
      let courseId = null;
      
      if (idMatches && idMatches[1]) {
        courseId = idMatches[1];
      }
      
      // If no ID found, try to find by name
      if (!courseId) {
        const nameMatches = prompt.match(/course\s+"([^"]+)"/) || 
                          prompt.match(/course\s+([^\s,]+)/) ||
                          prompt.match(/named\s+"([^"]+)"/) ||
                          prompt.match(/named\s+([^\s,]+)/) ||
                          prompt.match(/called\s+"([^"]+)"/) ||
                          prompt.match(/called\s+([^\s,]+)/);
        
        if (nameMatches && nameMatches[1]) {
          const courseName = nameMatches[1];
          try {
            // Fetch all courses to find the one with matching name
            const token = localStorage.getItem('token');
            const response = await axios.get('https://api.kewaar.com/api/admin/courses2', {
              headers: {
                'Authorization': `Bearer ${token}`,
                'Accept': 'application/json'
              }
            });
            
            if (response.data.success && response.data.data) {
              const matchingCourse = response.data.data.find(course => 
                course.title.toLowerCase() === courseName.toLowerCase()
              );
              
              if (matchingCourse) {
                courseId = matchingCourse.id;
              } else {
                return {
                  action: null,
                  message: `Could not find a course named "${courseName}". Please check the course name or use the course ID instead.`
                };
              }
            }
          } catch (error) {
            return {
              action: null,
              message: `Failed to search for course: ${error.response?.data?.message || error.message}`
            };
          }
        }
      }
      
      if (!courseId) {
        return {
          action: null,
          message: "I couldn't find a course ID or name in your command. Please specify which course to update by including its ID or exact name."
        };
      }
      
      // Extract update fields
      let updates = {};
      
      // Title update
      const titleMatches = prompt.match(/title\s+to\s+"([^"]+)"/) || 
                           prompt.match(/title\s+to\s+([^\s,]+)/) || 
                           prompt.match(/change\s+to\s+"([^"]+)"/);
      
      if (titleMatches && titleMatches[1]) {
        updates.title = titleMatches[1];
      }
      
      // Description update
      const descMatches = prompt.match(/description\s+to\s+"([^"]+)"/) || 
                          prompt.match(/description\s+to\s+(.+?)(?:,|\s+and\s+|$)/);
      
      if (descMatches && descMatches[1]) {
        updates.description = descMatches[1];
      }
      
      if (Object.keys(updates).length === 0) {
        return {
          action: null,
          message: "I couldn't determine what you want to update. Please specify the fields to update, like title or description."
        };
      }
      
      try {
        const token = localStorage.getItem('token');
        const formData = new FormData();
        
        Object.keys(updates).forEach(key => {
          formData.append(key, updates[key]);
        });
        
        await axios.post(`https://api.kewaar.com/api/admin/courses/${courseId}`, 
          formData,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Accept': 'application/json',
              'X-HTTP-Method-Override': 'PUT'
            }
          }
        );
        
        return {
          action: 'update',
          message: `Successfully updated course ${courseId}`
        };
      } catch (error) {
        return {
          action: null,
          message: `Failed to update course: ${error.response?.data?.message || error.message}`
        };
      }
    },
    
    async handleDeleteCommand(prompt) {
      // Extract course ID
      const idMatches = prompt.match(/id\s+(\d+)/) || prompt.match(/course\s+(\d+)/);
      let courseId = null;
      
      if (idMatches && idMatches[1]) {
        courseId = idMatches[1];
      }
      
      if (!courseId) {
        return {
          action: null,
          message: "I couldn't find a course ID in your command. Please specify which course to delete by including its ID."
        };
      }
      
      try {
        const token = localStorage.getItem('token');
        
        await axios.delete(`https://api.kewaar.com/api/admin/courses/${courseId}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Accept': 'application/json'
          }
        });
        
        return {
          action: 'delete',
          message: `Successfully deleted course with ID ${courseId}`
        };
      } catch (error) {
        return {
          action: null,
          message: `Failed to delete course: ${error.response?.data?.message || error.message}`
        };
      }
    },
    
    async handleStatusCommand(prompt) {
      // Extract course ID
      const idMatches = prompt.match(/id\s+(\d+)/) || prompt.match(/course\s+(\d+)/);
      let courseId = null;
      
      if (idMatches && idMatches[1]) {
        courseId = idMatches[1];
      }
      
      if (!courseId) {
        return {
          action: null,
          message: "I couldn't find a course ID in your command. Please specify which course to update by including its ID."
        };
      }
      
      // Determine status
      let status = null;
      if (prompt.includes('publish')) {
        status = 'published';
      } else if (prompt.includes('draft')) {
        status = 'draft';
      }
      
      if (!status) {
        return {
          action: null,
          message: "I couldn't determine the status you want to set. Please specify 'published' or 'draft'."
        };
      }
      
      try {
        const token = localStorage.getItem('token');
        const formData = new FormData();
        formData.append('status', status);
        
        await axios.post(`https://api.kewaar.com/api/admin/courses/${courseId}/status`, 
          formData,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Accept': 'application/json'
            }
          }
        );
        
        return {
          action: 'status',
          message: `Successfully updated course ${courseId} status to ${status}`
        };
      } catch (error) {
        return {
          action: null,
          message: `Failed to update course status: ${error.response?.data?.message || error.message}`
        };
      }
    },
    
    handleListCommand(prompt) {
      let filteredCourses = [...this.courses];
      let message = '';
      
      // Filter by status
      if (prompt.includes('published')) {
        filteredCourses = filteredCourses.filter(course => course.status === 'published');
        message = `Found ${filteredCourses.length} published courses:`;
      } else if (prompt.includes('draft')) {
        filteredCourses = filteredCourses.filter(course => course.status === 'draft');
        message = `Found ${filteredCourses.length} draft courses:`;
      } else {
        message = `Showing all ${filteredCourses.length} courses:`;
      }
      
      // Add course list to message
      if (filteredCourses.length > 0) {
        message += '<br><ul>';
        filteredCourses.forEach(course => {
          message += `<li><strong>ID ${course.id}: ${course.title}</strong> - ${course.status} (${course.lessons_count || 0} lessons)</li>`;
        });
        message += '</ul>';
      } else {
        message += '<br>No courses found matching your criteria.';
      }
      
      return {
        action: 'list',
        message: message
      };
    },
    
    addUserMessage(content) {
      this.chatHistory.push({
        type: 'user',
        content
      });
      this.scrollToBottom();
    },
    
    addAssistantMessage(content) {
      this.chatHistory.push({
        type: 'assistant',
        content
      });
      this.scrollToBottom();
    },
    
    addSystemMessage(content) {
      this.chatHistory.push({
        type: 'system',
        content
      });
      this.scrollToBottom();
    },
    
    scrollToBottom() {
      this.$nextTick(() => {
        const chatContainer = document.querySelector('.chat-messages');
        if (chatContainer) {
          chatContainer.scrollTop = chatContainer.scrollHeight;
        }
      });
    },
    
    fillPrompt(prompt) {
      this.userPrompt = prompt;
      this.$nextTick(() => {
        document.querySelector('textarea').focus();
      });
    },
    
    truncateDescription(description) {
      if (!description) return '';
      // Strip HTML
      const text = description.replace(/<[^>]*>/g, '');
      return text.length > 100 ? text.substring(0, 97) + '...' : text;
    }
  }
};
</script>

<style scoped>
.course-gpt {
  display: flex;
  min-height: 100vh;
  background-color: #f8f9fa;
}

.gpt-container {
  flex: 1;
  margin-left: 250px;
  padding: 2rem;
  display: flex;
  flex-direction: column;
  gap: 2rem;
}

.header {
  text-align: center;
  margin-bottom: 1rem;
}

.header h1 {
  font-size: 2rem;
  color: #3b82f6;
  margin-bottom: 0.5rem;
}

.subtitle {
  color: #64748b;
  font-size: 1.1rem;
}

.courses-display {
  background-color: white;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.courses-display h2 {
  margin-bottom: 1rem;
  font-size: 1.5rem;
  color: #1e293b;
}

.courses-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 1.5rem;
}

.course-card {
  position: relative;
  border-radius: 8px;
  overflow: hidden;
  background-color: white;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.course-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.status-badge {
  position: absolute;
  top: 10px;
  right: 10px;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 0.75rem;
  text-transform: uppercase;
  font-weight: 600;
  z-index: 1;
}

.status-badge.active {
  background-color: #10b981;
  color: white;
}

.status-badge.inactive {
  background-color: #9ca3af;
  color: white;
}

.course-image {
  width: 100%;
  height: 140px;
  object-fit: cover;
}

.course-image-placeholder {
  width: 100%;
  height: 140px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #e5e7eb;
}

.course-image-placeholder svg {
  width: 40px;
  height: 40px;
  color: #9ca3af;
}

.course-content {
  padding: 1rem;
}

.course-content h3 {
  margin: 0 0 0.5rem 0;
  font-size: 1.1rem;
  color: #1e293b;
}

.course-lessons {
  color: #64748b;
  font-size: 0.8rem;
  margin-bottom: 0.5rem;
}

.course-description {
  color: #4b5563;
  font-size: 0.9rem;
  line-height: 1.4;
}

.chat-interface {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  background-color: white;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.chat-messages {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  max-height: 300px;
  overflow-y: auto;
  padding: 0.5rem;
}

.message {
  display: flex;
  gap: 0.75rem;
  padding: 0.75rem;
  border-radius: 8px;
  font-size: 0.95rem;
}

.user-message {
  background-color: #e0f2fe;
  margin-left: 2rem;
}

.assistant-message {
  background-color: #f1f5f9;
  margin-right: 2rem;
}

.system-message {
  background-color: #fef3c7;
  font-style: italic;
}

.message-icon {
  flex-shrink: 0;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.message-content {
  flex-grow: 1;
}

.user-input {
  display: flex;
  gap: 0.5rem;
  margin: 1rem 0;
}

.user-input textarea {
  flex-grow: 1;
  padding: 0.75rem;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 0.95rem;
  resize: none;
}

.user-input button {
  padding: 0 1rem;
  background-color: #3b82f6;
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.user-input button:hover:not(:disabled) {
  background-color: #2563eb;
}

.user-input button:disabled {
  background-color: #94a3b8;
  cursor: not-allowed;
}

.command-examples {
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid #e2e8f0;
}

.command-examples h3 {
  font-size: 1rem;
  color: #64748b;
  margin-bottom: 0.75rem;
}

.command-examples ul {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.command-examples li {
  padding: 0.5rem;
  background-color: #f8fafc;
  border-radius: 6px;
  border: 1px solid #e2e8f0;
  font-size: 0.85rem;
  color: #334155;
  cursor: pointer;
}

.command-examples li:hover {
  background-color: #f1f5f9;
  border-color: #cbd5e1;
}

@media (max-width: 768px) {
  .gpt-container {
    margin-left: 0;
    padding: 1rem;
  }
  
  .courses-grid {
    grid-template-columns: 1fr;
  }
}
</style> 